import { FormEvent, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { SearchHit } from "@itaas/pep-contracts";
import { searchAll } from "../api/pep-api";
import { SiteLayout } from "../components/layout/SiteLayout";
import { SeoHead } from "../components/seo/SeoHead";
import { usePepI18n } from "../i18n/usePepI18n";

export function SearchPage() {
  const { t, locale } = usePepI18n();
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState(params.get("q") ?? "");
  const [results, setResults] = useState<SearchHit[]>([]);
  const [loading, setLoading] = useState(false);

  const typeLabels: Record<SearchHit["type"], string> = {
    product: t("search.type.product"),
    solution: t("search.type.solution"),
    industry: t("search.type.industry"),
    doc: t("search.type.doc"),
    marketplace: t("search.type.marketplace"),
    partner: t("search.type.partner"),
  };

  useEffect(() => {
    const q = params.get("q") ?? "";
    setQuery(q);
    if (q.length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    searchAll(q)
      .then(setResults)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [params]);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setParams(query.trim() ? { q: query.trim() } : {});
  }

  return (
    <SiteLayout>
      <SeoHead
        title={t("search.title")}
        description={t("search.placeholder")}
        canonicalPath="/search"
        locale={locale}
      />
      <div className="container py-5">
        <h1 className="display-6 fw-bold mb-4">{t("search.title")}</h1>
        <form onSubmit={onSubmit} className="mb-4 col-lg-8">
          <div className="input-group input-group-lg">
            <input
              className="form-control"
              placeholder={t("search.placeholder")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
            <button type="submit" className="btn btn-primary">
              {t("search.submit")}
            </button>
          </div>
        </form>

        {loading && <p className="text-secondary">{t("search.loading")}</p>}

        {!loading && params.get("q") && results.length === 0 && (
          <p className="text-secondary">{t("search.noResults", { query: params.get("q") ?? "" })}</p>
        )}

        <div className="list-group col-lg-8">
          {results.map((hit) => (
            <Link key={`${hit.type}-${hit.id}`} to={hit.url} className="list-group-item list-group-item-action">
              <div className="d-flex justify-content-between">
                <span className="fw-semibold">{hit.title}</span>
                <span className="badge text-bg-secondary">{typeLabels[hit.type]}</span>
              </div>
              <div className="small text-secondary">{hit.excerpt}</div>
            </Link>
          ))}
        </div>
      </div>
    </SiteLayout>
  );
}
